<template>
  <PartnersLayout>
    <!-- 헤더 -->
    <PartnersHeader
      title="심사 결과 확인"
      subtitle="현장 관리"
    />

    <!-- 메인 컨텐츠 -->
    <div class="px-4 lg:px-6 pb-8">
      <div class="max-w-6xl mx-auto">
        <!-- 3컬럼 그리드 -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 왼쪽: 필터 & 요약 (1/3) -->
          <div class="lg:col-span-1 space-y-4">
            <!-- 필터 카드 -->
            <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <h3 class="text-sm font-semibold text-slate-500 mb-4">상태 필터</h3>
              <div class="space-y-2">
                <button
                  v-for="tab in tabs"
                  :key="tab.value"
                  @click="activeTab = tab.value"
                  :class="[
                    'w-full flex items-center justify-between px-4 py-3 rounded-xl transition',
                    activeTab === tab.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                  ]"
                >
                  <span class="font-medium">{{ tab.label }}</span>
                  <span :class="[
                    'px-2.5 py-0.5 rounded-full text-xs font-semibold',
                    activeTab === tab.value
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-200 text-slate-600'
                  ]">
                    {{ getCountByStatus(tab.value) }}
                  </span>
                </button>
              </div>
            </div>

            <!-- 요약 카드 -->
            <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <h3 class="text-sm font-semibold text-slate-500 mb-4">신청 현황</h3>
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <span class="text-slate-600">전체 신청</span>
                  <span class="font-bold text-slate-900">{{ applications.length }}건</span>
                </div>
                <div class="h-px bg-slate-100"></div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-amber-500"></div>
                    <span class="text-slate-600">대기중</span>
                  </div>
                  <span class="font-medium text-amber-600">{{ getCountByStatus('pending') }}건</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-emerald-500"></div>
                    <span class="text-slate-600">승인</span>
                  </div>
                  <span class="font-medium text-emerald-600">{{ getCountByStatus('approved') }}건</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-red-500"></div>
                    <span class="text-slate-600">거절</span>
                  </div>
                  <span class="font-medium text-red-600">{{ getCountByStatus('rejected') }}건</span>
                </div>
              </div>
            </div>

            <!-- 새 신청 버튼 -->
            <router-link
              to="/partners/property/change"
              class="flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              새 현장 신청
            </router-link>
          </div>

          <!-- 오른쪽: 신청 내역 목록 (2/3) -->
          <div class="lg:col-span-2">
            <!-- 로딩 -->
            <div v-if="loading" class="bg-white rounded-2xl p-12 shadow-sm border border-slate-100 text-center">
              <div class="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p class="text-slate-500">신청 내역을 불러오는 중...</p>
            </div>

            <!-- 신청 내역 목록 -->
            <div v-else-if="filteredApplications.length > 0" class="space-y-4">
              <div
                v-for="app in filteredApplications"
                :key="app.id"
                class="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden"
              >
                <!-- 카드 헤더 -->
                <div class="flex items-center justify-between p-4 border-b border-slate-100">
                  <div class="flex items-center gap-3">
                    <span :class="[
                      'px-3 py-1 rounded-full text-xs font-semibold',
                      app.status === 'pending' ? 'bg-amber-100 text-amber-700' : '',
                      app.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : '',
                      app.status === 'rejected' ? 'bg-red-100 text-red-700' : ''
                    ]">
                      {{ getStatusLabel(app.status) }}
                    </span>
                    <h3 class="font-semibold text-slate-800">{{ app.propertyName }}</h3>
                  </div>
                  <span class="text-sm text-slate-400">{{ formatDate(app.createdAt) }}</span>
                </div>

                <!-- 카드 본문 -->
                <div class="p-4">
                  <!-- 변경 신청인 경우 -->
                  <div v-if="app.isChange && app.previousPropertyName" class="flex items-center gap-2 p-3 bg-slate-50 rounded-xl mb-4 text-sm">
                    <span class="text-slate-500">{{ app.previousPropertyName }}</span>
                    <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                    </svg>
                    <span class="font-medium text-blue-600">{{ app.propertyName }}</span>
                  </div>

                  <!-- 영업 정보 -->
                  <div class="grid grid-cols-2 gap-3 mb-4">
                    <div class="bg-slate-50 rounded-xl p-3">
                      <span class="block text-xs text-slate-500 mb-1">영업명</span>
                      <span class="block text-sm font-medium text-slate-800">{{ app.displayName }}</span>
                    </div>
                    <div class="bg-slate-50 rounded-xl p-3">
                      <span class="block text-xs text-slate-500 mb-1">한 줄 소개</span>
                      <span class="block text-sm font-medium text-slate-800">{{ app.introduction }}</span>
                    </div>
                  </div>

                  <!-- 변경 사유 (있는 경우) -->
                  <div v-if="app.changeReason" class="p-3 bg-blue-50 rounded-xl mb-4">
                    <span class="block text-xs text-blue-600 font-medium mb-1">변경 사유</span>
                    <p class="text-sm text-blue-800">{{ app.changeReason }}</p>
                  </div>

                  <!-- 거절 사유 (거절된 경우) -->
                  <div v-if="app.status === 'rejected' && app.rejectReason" class="p-3 bg-red-50 rounded-xl">
                    <span class="flex items-center gap-1 text-xs text-red-600 font-medium mb-1">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                      </svg>
                      거절 사유
                    </span>
                    <p class="text-sm text-red-800">{{ app.rejectReason }}</p>
                  </div>

                  <!-- 승인된 경우 -->
                  <div v-if="app.status === 'approved'" class="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl">
                    <svg class="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <div>
                      <p class="font-medium text-emerald-800">승인되었습니다</p>
                      <p class="text-sm text-emerald-600">승인일: {{ formatDate(app.approvedAt) }}</p>
                    </div>
                  </div>

                  <!-- 대기중인 경우 -->
                  <div v-if="app.status === 'pending'" class="flex items-center gap-3 p-3 bg-amber-50 rounded-xl">
                    <svg class="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <p class="text-sm text-amber-800">관리자 검토 중입니다. 잠시만 기다려주세요.</p>
                  </div>
                </div>

                <!-- 카드 푸터 -->
                <div class="flex items-center justify-between p-4 bg-slate-50 border-t border-slate-100">
                  <button @click="openImageModal(app.businessCardUrl)" class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                    </svg>
                    제출 명함 보기
                  </button>

                  <!-- 거절된 경우 재신청 버튼 -->
                  <router-link
                    v-if="app.status === 'rejected'"
                    to="/partners/property/change"
                    class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                    다시 신청하기
                  </router-link>
                </div>
              </div>
            </div>

            <!-- 빈 상태 -->
            <div v-else class="bg-white rounded-2xl p-12 shadow-sm border border-slate-100 text-center">
              <div class="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
              </div>
              <p class="text-lg font-semibold text-slate-800 mb-2">{{ getEmptyMessage }}</p>
              <p class="text-slate-500 mb-6">소속 신청 내역이 여기에 표시됩니다</p>
              <router-link v-if="activeTab !== 'pending'" to="/partners/property/change" class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition font-medium">
                현장 신청하기
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 이미지 모달 -->
    <Teleport to="body">
      <div v-if="showImageModal" class="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" @click="closeImageModal">
        <div class="relative max-w-3xl w-full" @click.stop>
          <button @click="closeImageModal" class="absolute -top-12 right-0 p-2 text-white hover:text-slate-300 transition">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
          <img :src="selectedImageUrl" alt="명함" class="w-full rounded-xl" />
        </div>
      </div>
    </Teleport>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'

export default {
  name: 'ApplicationHistoryView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const { user } = useAuth()
    const loading = ref(true)
    const applications = ref([])
    const activeTab = ref('all')
    const showImageModal = ref(false)
    const selectedImageUrl = ref('')

    const tabs = [
      { label: '전체', value: 'all' },
      { label: '대기중', value: 'pending' },
      { label: '승인', value: 'approved' },
      { label: '거절', value: 'rejected' }
    ]

    const filteredApplications = computed(() => {
      if (activeTab.value === 'all') return applications.value
      return applications.value.filter(app => app.status === activeTab.value)
    })

    const getEmptyMessage = computed(() => {
      const messages = {
        all: '신청 내역이 없습니다',
        pending: '대기중인 신청이 없습니다',
        approved: '승인된 신청이 없습니다',
        rejected: '거절된 신청이 없습니다'
      }
      return messages[activeTab.value] || '신청 내역이 없습니다'
    })

    const fetchApplications = async () => {
      if (!user.value) return

      try {
        const q = query(
          collection(db, 'partnerApplications'),
          where('partnerId', '==', user.value.uid),
          orderBy('createdAt', 'desc')
        )
        const snapshot = await getDocs(q)
        applications.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (error) {
        console.error('신청 내역 로드 실패:', error)
      }
    }

    const getCountByStatus = (status) => {
      if (status === 'all') return applications.value.length
      return applications.value.filter(app => app.status === status).length
    }

    const getStatusLabel = (status) => {
      const labels = {
        pending: '심사중',
        approved: '승인',
        rejected: '거절'
      }
      return labels[status] || status
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
    }

    const openImageModal = (url) => {
      selectedImageUrl.value = url
      showImageModal.value = true
    }

    const closeImageModal = () => {
      showImageModal.value = false
      selectedImageUrl.value = ''
    }

    onMounted(async () => {
      loading.value = true
      await fetchApplications()
      loading.value = false
    })

    return {
      loading,
      applications,
      activeTab,
      tabs,
      filteredApplications,
      getEmptyMessage,
      showImageModal,
      selectedImageUrl,
      getCountByStatus,
      getStatusLabel,
      formatDate,
      openImageModal,
      closeImageModal
    }
  }
}
</script>
